import React from "react";
import { convertDate } from "@Services/luxonHelper";
import { getStatusBtnStyle } from "@Features/reviews";
import { getImageUrl } from "@Utils";
import { getFileIcon } from "@CubeComponents/constants/globalFunctions";
import { t } from "@i18n";
import { renderUserTag, renderUserTagGroup } from "@CubeComponents/html/userTagDom";
import { mountCellPortal } from "@CubeComponents/html/cellPortals";
import TextboxIcon from "../../../../assets/icons/svg/Textbox.svg";
import MultiLineIcon from "../../../../assets/icons/svg/Multi Line.svg";
import DateAndTimeIcon from "../../../../assets/icons/svg/Date and Time.svg";
import DropdownIcon from "../../../../assets/icons/svg/Dropdown.svg";
import IDIcon from "../../../../assets/icons/svg/ID.svg";
import Image2Icon from "../../../../assets/icons/svg/Image2.svg";
import UserProfileIcon from "../../../../assets/icons/svg/User Profile.svg";
import VersionIcon from "../../../../assets/icons/svg/Version.svg";
import FilesIcon from "../../../../assets/icons/svg/files.svg";
import NumericIcon from "../../../../assets/icons/svg/Numeric.svg";
import ObjectTree from "../../../../assets/icons/svg/SidebarIcon/Object Tree.svg";
import _ from "lodash";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

/* ------------------------------------------------------------------
    Header With Icon
------------------------------------------------------------------ */
interface HeaderWithIconProps {
  icon: string;
  label: string;
  css?: string;
}

export const IconMap: Record<string, string> = {
  Textbox: TextboxIcon,
  "Multi Line": MultiLineIcon,
  "Date and Time": DateAndTimeIcon,
  Dropdown: DropdownIcon,
  ID: IDIcon,
  Image2: Image2Icon,
  "User Profile": UserProfileIcon,
  Version: VersionIcon,
  files: FilesIcon,
  Numeric: NumericIcon,
  directory: ObjectTree,
};

export interface FormatterDateColDef {
  field: string;
  formatterType?: string;
  filterType?: string;
  name?: string;
  /** Can be a function or a legacy string field path */
  _valueGetter?: ((ctx: Record<string, unknown>) => unknown) | string;
}

/** Minimal column def shape used by formatters in this file */
export interface FormatterColDef {
  field?: string;
  formatterType?: string;
  filterType?: string;
  name?: string;
  /** Can be a function or a legacy string field path */
  _valueGetter?: ((ctx: Record<string, unknown>) => unknown) | string;
  _imageGetter?: (ctx: Record<string, unknown>) => string | undefined;
}

/** Minimal grid instance shape used by formatters */
export interface GridLike {
  getOptions?: () => { rowHeight?: number };
}

export type DataContext = Record<string, unknown>;

const getValue = (column: FormatterColDef, dataContext: DataContext) => {
  const field = column?.field;
  if (typeof column._valueGetter === "function") {
    return column._valueGetter(dataContext);
  }
  // Fallback: support legacy string path for _valueGetter (treated like a field path)
  if (typeof column._valueGetter === "string") {
    return _.get(dataContext, column._valueGetter as string);
  }
  return field !== undefined ? _.get(dataContext, field) : undefined;
};

// HeaderWithIcon is rendered via renderToStaticMarkup (SSR) — must not use hooks or MUI components
export const HeaderWithIcon: React.FC<HeaderWithIconProps> = ({ icon, label, css = "" }) => {
  const iconSrc = IconMap[icon];

  if (!iconSrc) {
    console.warn(`Icon not found in map for key: ${icon}`);
    return <div className="truncate ms-1">{label}</div>;
  }

  return (
    <div className="custom-header flex max-w-full items-center justify-center gap-x-1 ms-1" title={label}>
      <img src={iconSrc} className={`w-4 h-4 shrink-0 ${css}`} alt={label} />
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontFamily: "inherit",
        }}
      >
        {label}
      </span>
    </div>
  );
};

/* ------------------------------------------------------------------
    Infer Icon For Column
------------------------------------------------------------------ */
export function inferIconForColumn(column: FormatterColDef): string {
  // By formatterType (highest priority)
  if (column.formatterType === "Date") return "Date and Time";
  if (column.formatterType === "image") return "Image2";
  if (column.formatterType === "ID") return "ID";
  if (column.formatterType === "userTag") return "User Profile";
  if (column.formatterType === "status") return "Dropdown";
  if (column.formatterType === "file") return "files";
  if (column.formatterType === "description") return "Multi Line";

  // By filterType
  if (column.filterType === "Date" || column.filterType === "date") return "Date and Time";

  // By field name patterns
  const field = String(column.field || "").toLowerCase();
  const name = String(column.name || "").toLowerCase();
  const combined = `${field} ${name}`;

  if (/\b(date|time|created[_]?on|modified[_]?on|due[_]?date|initiated)\b/.test(combined)) return "Date and Time";
  if (/\b(status|severity|priority|category|type|level)\b/.test(combined) && !combined.includes("id"))
    return "Dropdown";
  if (/\b(created[_]?by|assigned|owner|modified[_]?by|user[_]?name|fullname)\b/.test(combined)) return "User Profile";
  if (/\b(image|snapshot|thumbnail|avatar|photo)\b/.test(combined)) return "Image2";
  if (/\b(description|notes|comment|remark|body|content)\b/.test(combined)) return "Multi Line";
  if (/\b(weightage|weight)\b/.test(combined)) return "Numeric";
  if (/\b(version)\b/.test(combined)) return "Version";
  if (/\b(file)\b/.test(combined) && !combined.includes("id")) return "files";
  if (/\bid\b|counter|serial|number|index/.test(combined)) return "ID";

  // Default: text
  return "Textbox";
}

/* ------------------------------------------------------------------
    Date Formatter
------------------------------------------------------------------ */
export function formatDateField(
  _row: number,
  _cell: number,
  _value: unknown,
  colDef: FormatterDateColDef,
  dataContext: DataContext,
) {
  const container = document.createElement("div");
  const value = _.get(dataContext, colDef.field);

  if (!value) {
    container.textContent = "-";
    return container;
  }

  const timestamp = Number(value);

  if (Number.isNaN(timestamp)) {
    container.textContent = "-";
    return container;
  }

  const tooltipFormat = convertDate(timestamp, "dateForTooltip");
  const displayFormat = convertDate(timestamp, "dateForText");
  container.style.width = "100%";
  container.style.overflow = "hidden";

  // [plain-DOM] no React container → zero React event-delegation listeners; native title tooltip
  container.style.cssText = "width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;";
  container.textContent = String(displayFormat);
  container.setAttribute("data-cube-tip", String(tooltipFormat));
  return container;
}

export function imageFormatter(
  _row: number,
  _cell: number,
  _value: unknown,
  colDef: FormatterColDef,
  dataContext: DataContext,
  grid?: GridLike,
) {
  const imageIdOrUrl = colDef._imageGetter?.(dataContext);

  const rowHeight = grid?.getOptions?.()?.rowHeight || 48;

  const getSizeConfig = (height: number) => {
    if (height <= 48) {
      return { imageWidth: 34, imageHeight: 22, padding: "0.0625rem", border: "0.75px" };
    } else if (height >= 96) {
      return { imageWidth: 68, imageHeight: 44, padding: "0.125rem", border: "0.0625rem" };
    }
    return { imageWidth: 48, imageHeight: 32, padding: "0.125rem", border: "0.0625rem" };
  };

  const config = getSizeConfig(rowHeight);

  const container = document.createElement("div");
  container.style.display = "flex";
  container.style.alignItems = "center";
  container.style.justifyContent = "center";
  container.style.width = "100%";
  container.style.height = "100%";

  const src = imageIdOrUrl || `${getImageUrl()}/Issues_default_image.svg`; // fallback

  // [plain-DOM] no React container → zero React event-delegation listeners
  const wrapper = document.createElement("div");
  wrapper.style.border = `${config.border} solid #DCE8F5`;
  wrapper.style.borderRadius = "0.25rem";
  wrapper.style.padding = config.padding;
  const img = document.createElement("img");
  img.src = src;
  img.alt = t("image");
  img.style.objectFit = "contain";
  img.style.width = `${config.imageWidth / 16}rem`;
  img.style.height = `${config.imageHeight / 16}rem`;
  img.onerror = () => {
    img.src = `${getImageUrl()}/Issues_default_image.svg`;
  };
  wrapper.appendChild(img);
  container.appendChild(wrapper);
  return container;
}

export function IDcounter(
  _row: number,
  _cell: number,
  _value: unknown,
  columnDef: FormatterColDef,
  dataContext: DataContext,
) {
  const container = document.createElement("div");
  const value = getValue(columnDef, dataContext);

  if (!value) {
    container.textContent = "-";
    return container;
  }

  // [plain-DOM] no React container → zero React event-delegation listeners
  const valStr = String(value) || "-";
  container.className = "flex items-center gap-1 min-w-0";
  const span = document.createElement("span");
  span.className = "truncate";
  span.textContent = valStr;
  span.setAttribute("data-cube-tip", valStr);
  container.appendChild(span);
  if (!dataContext.isFormTemplateValid) {
    const err = document.createElement("span");
    err.className = "shrink-0";
    err.style.color = "#d32f2f";
    err.textContent = "⚠";
    err.setAttribute("data-cube-tip", t("Mandatory custom fields not filled."));
    container.appendChild(err);
  }
  return container;
}

export function fileFormatter(
  _row: number,
  _cell: number,
  _value: unknown,
  columnDef: FormatterColDef,
  dataContext: DataContext,
) {
  const container = document.createElement("div");
  const val = getValue(columnDef, dataContext);

  if (!val) {
    container.textContent = "-";
    return container;
  }

  const valStr = String(val);
  const ext = valStr.split(".").pop()?.toLowerCase() ?? "";

  return mountCellPortal(
    container,
    <div className="flex items-center gap-2 border border-[#DCE8F5] rounded p-2">
      <span className="inline-flex items-center">{getFileIcon(ext)}</span>
      <span className="inline-block truncate flex-1 min-w-0" data-cube-tip={valStr}>
        {valStr}
      </span>
    </div>,
  );
}

export function userTag(
  _row: number,
  _cell: number,
  _b: unknown,
  columnDef: FormatterColDef,
  dataContext: DataContext,
) {
  const container = document.createElement("div");
  // Declaration site for the account id: a SlickGrid row is an untyped `Record<string, unknown>`
  // and the id is read by the column's configured field path, so this is the one point where the
  // value crosses into the type system.
  const id = getValue(columnDef, dataContext) as ObjectIdString | undefined;
  if (!id) {
    container.textContent = "-";
    return container;
  }
  // [plain-DOM] no React/MUI/Redux-subscription per cell (see userTagDom.ts)
  return renderUserTag(container, { id });
}

export function userTagGroup(
  _row: number,
  _cell: number,
  _b: unknown,
  columnDef: FormatterColDef,
  dataContext: DataContext,
) {
  const container = document.createElement("div");
  const field = columnDef?.field;
  const value = field ? _.get(dataContext, field) : "";
  if (!value || !Array.isArray(value) || !value.length) {
    container.textContent = "-";
    return container;
  }
  // [plain-DOM] no React/MUI/Redux-subscription per cell (see userTagDom.ts)
  if (value.length === 1) {
    return renderUserTag(container, { id: value[0]._id });
  }
  return renderUserTagGroup(container, {
    ids: value.map((u: { _id: ObjectIdString }) => u._id),
    maxAvatars: 4,
    size: "XXS",
    enableDetailsModal: true,
    detailsModalHeader: t("Assigned To"),
  });
}

export function formatSeverity(
  _row: number,
  _cell: number,
  _b: unknown,
  columnDef: FormatterColDef,
  dataContext: DataContext,
) {
  const container = document.createElement("div");
  const value = getValue(columnDef, dataContext);

  if (!value) {
    container.textContent = "-";
    return container;
  }

  // [plain-DOM] no React container → zero React event-delegation listeners
  const labels: Record<string, string> = { MINOR: t("Minor"), MAJOR: t("Major") };
  const valueStr = String(value);
  container.textContent = labels[valueStr] ?? valueStr;
  container.setAttribute("data-cube-tip", valueStr);
  return container;
}

interface StatusInReview {
  _id?: string;
  category?: string;
  name?: string;
}

export function statusFormatter(
  _row: number,
  _cell: number,
  _value: unknown,
  _columnDef: FormatterColDef,
  dataContext: DataContext,
) {
  const statusInReview = dataContext.statusInReview as StatusInReview | undefined;
  const container = document.createElement("div");
  container.style.display = "inline-block";
  container.style.width = "100%";

  if (!statusInReview?._id) {
    container.textContent = "-";
    return container;
  }

  // [plain-DOM] no React container → zero React event-delegation listeners
  const style = getStatusBtnStyle(statusInReview.category) as Record<string, string>;
  const chip = document.createElement("div");
  chip.className = "w-fit rounded py-1.5 px-4 font-semibold text-white";
  Object.assign(chip.style, style);
  chip.style.display = "flex";
  chip.style.alignItems = "center";
  chip.style.justifyContent = "space-between";
  chip.style.width = "100%";
  chip.style.padding = "0.5rem";
  chip.style.lineHeight = "1";
  chip.textContent = statusInReview.name ?? "";
  chip.setAttribute("data-cube-tip", statusInReview.name ?? "");
  container.appendChild(chip);
  return container;
}

export function descriptionTemplate(
  _r: number,
  _c: number,
  _v: unknown,
  _col: FormatterColDef,
  dataContext: DataContext,
) {
  const container = document.createElement("div");
  container.style.cssText = "width:100%; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; margin:auto 0;";
  // [plain-DOM] no React container → zero React event-delegation listeners
  const description = (dataContext as { description?: string }).description;
  if (description && description.length > 0) {
    const doc = new DOMParser().parseFromString(description, "text/html");
    container.textContent = doc.body.textContent || "";
    container.setAttribute("data-cube-tip", doc.body.textContent || "");
  } else {
    container.textContent = "-";
  }
  return container;
}
